import { useEffect, useRef, useState } from "react";
import type { ClientMessage, ServerMessage } from "@shared/protocol";

export type WSStatus = "connecting" | "open" | "closed";

const WS_URL = (() => {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws`;
})();

interface Options {
  token: string | null;
  onMessage: (msg: ServerMessage) => void;
}

export function useWebSocket({ token, onMessage }: Options) {
  const [status, setStatus] = useState<WSStatus>("connecting");
  const wsRef = useRef<WebSocket | null>(null);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (cancelled) return;
      setStatus("connecting");
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      ws.onopen = () => {
        setStatus("open");
        ws.send(JSON.stringify({ type: "auth", payload: { token } } satisfies ClientMessage));
      };
      ws.onmessage = (ev) => {
        try {
          handlerRef.current(JSON.parse(ev.data) as ServerMessage);
        } catch (err) {
          console.warn("[ws] bad message", err);
        }
      };
      ws.onclose = () => {
        setStatus("closed");
        if (!cancelled) {
          reconnectTimer = setTimeout(connect, 1500);
        }
      };
      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, [token]);

  const send = (msg: ClientMessage) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(msg));
    return true;
  };

  return { status, send };
}
